import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoType, setLogoType] = useState('kolej');
  const [settings, setSettings] = useState({});
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const savedLogoType = localStorage.getItem('siteLogo') || 'kolej';
    setLogoType(savedLogoType);
    fetchSettings();
    updateFavicon(savedLogoType);
  }, [location.pathname]);

  const fetchSettings = async () => {
    try {
      const response = await fetch('http://localhost/api/settings.php', {
        credentials: 'include',
      });
      if (response.ok) {
        const text = await response.text();
        const data = text ? JSON.parse(text) : {};
        setSettings(data);
        const savedLogoType = localStorage.getItem('siteLogo') || 'kolej';
        updateFavicon(savedLogoType, data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const updateFavicon = (logoType, settingsData = settings) => {
    const faviconLink = document.querySelector("link[rel~='icon']") || document.createElement('link');
    faviconLink.rel = 'icon';
    const faviconUrl = logoType === 'kurs-merkezi'
      ? (settingsData.kurs_merkezi_favicon || '/favicon.ico')
      : (settingsData.kolej_favicon || '/favicon.ico');
    faviconLink.href = faviconUrl;
    if (!document.querySelector("link[rel~='icon']")) {
      document.getElementsByTagName('head')[0].appendChild(faviconLink);
    }
  };

  const isActive = (path) => location.pathname === path;

  const logoSrc = logoType === 'kurs-merkezi'
    ? (settings.kurs_merkezi_logo || '/kursmerkezi.png')
    : (settings.kolej_logo || '/logo.png');
  const logoAlt = logoType === 'kurs-merkezi' ? 'Kurs Merkezi' : 'Serpil Şahin Koleji';

  const navLinks = [
    { path: '/kolej', label: 'Ana Sayfa' },
    { path: '/hakkimizda', label: 'Hakkımızda' },
    { path: '/egitim-kademeleri', label: 'Eğitim Kademeleri' },
    { path: '/kabul', label: 'Kabul' },
    { path: '/iletisim', label: 'İletişim' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-xl shadow-2xl shadow-gray-200/50' 
          : 'bg-white/70 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/kolej" className="flex items-center space-x-3 group">
            <img 
              src={logoSrc} 
              alt={logoAlt} 
              className="h-20 w-auto object-contain"
              onError={(e) => {
                // Logo yüklenemezse fallback göster
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div className="hidden" style={{display: 'none'}}>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-900 to-red-800 flex items-center justify-center shadow-lg">
              <span className="text-2xl font-bold text-white">{logoType === 'kurs-merkezi' ? 'K' : 'S'}</span>
            </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  isActive(link.path)
                    ? 'text-white bg-gradient-to-r from-[#C0A062] to-[#9a7d4a] shadow-lg shadow-[#C0A062]/30'
                    : 'text-gray-700 hover:text-[#C0A062] hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:scale-105'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2.5 rounded-xl text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:scale-105 transition-all duration-300"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200/50 backdrop-blur-xl" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    isActive(link.path)
                      ? 'text-white bg-gradient-to-r from-[#C0A062] to-[#9a7d4a] shadow-lg'
                      : 'text-gray-700 hover:text-[#C0A062] hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;